import type { Auth0Client } from "@auth0/auth0-spa-js";

import type { BrandingConfig } from "@utilifeed/config/branding";
import type { Network, Substation } from "@utilifeed/core/models";
import { URLS } from "@config";
import { AxiosApiClient } from "@core/apis/base/apiClient";
import type {
  EquivalentPowerAggregate,
  MdslResponse,
  TemperatureAverages,
} from "@core/apis/shared/types";
import { normalizeTemperatureAverages } from "@core/apis/shared/types";

import { logger as baseLogger } from "../../logger";
import { InfoDataResponse } from "../MDSL";
import type { ISharedDataApi } from "../shared/interfaces/shared-data-api.interface";
import type { EventLogEntry } from "../shared/types/event-log.type";
import type { MeterDataComponent, SensorDataPerSubstation } from "../shared/types/meter-data.model";
import { EnergyKpiResponse } from "../shared/types/propai.model";
import type { RadiatorCurve } from "../shared/types/radiator-curve.model";
import type { InfoBlocksResponse } from "../shared/types/response.types";
import type { FlexControlRequest, FlexStation, Sensor } from "../shared/types/sensors.model";
import { FlexstationAdapterConfigBody, FlexStationParameters } from "../shared/types/sensors.model";
import { PropAIOrganization } from "./types";
import { PropertyOrganizationUser } from "./types/admin.type";
import { UrgentNotificationsResponse } from "./types/alerts.type";
import { FlexstationClimateSeriesPoint } from "./types/climate.type";
import { LiveInsightsResponse } from "./types/insights.type";

const logger = baseLogger.getSubLogger({ name: "propai.api" });

/**
 * Data API implementation for PropAI-web application
 * Uses REACT_APP_PROPAI_URL as the base URL
 */
export class PropAIApi extends AxiosApiClient implements ISharedDataApi {
  constructor(
    getTokenOrAuth0Client: (() => Promise<string>) | Auth0Client,
    authClient: Auth0Client | undefined = undefined
  ) {
    super(URLS.propai, getTokenOrAuth0Client, authClient);
  }

  /**
   * Defensive normalizer for API responses that may vary between:
   *  - response.data.data.collection
   *  - response.data.data (array)
   *  - response.data.collection
   *  - response.data (array)
   *
   * Returns an array (possibly empty) and never throws.
   */
  private static normalizeCollection(response: any): any[] {
    try {
      if (!response) return [];
      // Common nested patterns used across PropAI endpoints
      const d = response.data;
      if (!d) return [];
      // data.data.collection
      if (d.data && d.data.collection && Array.isArray(d.data.collection)) {
        return d.data.collection;
      }
      // data.data as array
      if (d.data && Array.isArray(d.data)) {
        return d.data;
      }
      // data.collection
      if (d.collection && Array.isArray(d.collection)) {
        return d.collection;
      }
      // data as array
      if (Array.isArray(d)) {
        return d;
      }
      // fallback: try to find first array-bearing property inside d
      for (const key of Object.keys(d)) {
        if (Array.isArray(d[key])) return d[key];
      }
      return [];
    } catch (e) {
      // In case of unexpected shape, return empty array rather than throwing
      return [];
    }
  }

  // user method
  async getUser(): Promise<any | null> {
    return this.client.get("/v1/user").then((response: MdslResponse) => {
      return response.data.data;
    });
  }

  // Network and Substation methods
  async getNetworks(): Promise<Network[]> {
    return this.client.get("/v1/networks").then((response: MdslResponse) => {
      return PropAIApi.normalizeCollection(response);
    });
  }

  // Organization methods

  /** Fetch all organizations the current user belongs to. */
  async getOrganizationList(): Promise<PropAIOrganization[]> {
    return this.client.get("/v1/organizations").then((response: MdslResponse) => {
      return PropAIApi.normalizeCollection(response);
    });
  }

  /**
   * Fetch branding configuration for a property organization.
   * GET /v1/property-organizations/{propertyOrganizationUID}/branding
   *
   * Returns null when no custom branding is configured (404) or on error,
   * allowing the caller to fall back to DEFAULT_BRANDING.
   *
   * Note: Logo paths are currently directory strings in the repo.
   * These will migrate to CDN URLs once the CDN is set up.
   */
  async getBranding(propertyOrganizationUid: string): Promise<BrandingConfig | null> {
    return this.client
      .get(`/v1/property-organizations/${propertyOrganizationUid}/branding`)
      .then((response) => response.data?.data?.branding ?? null)
      .catch((error) => {
        if (error.status === 404 || error.response?.status === 404) {
          return null;
        }
        logger.error("getBranding error", { error, propertyOrganizationUid });
        return null;
      });
  }

  async getSubstationByOrganization(orgId?: string): Promise<any> {
    return this.client
      .get(`/v1/organizations/${orgId}/substations`)
      .then((response: MdslResponse) => {
        return PropAIApi.normalizeCollection(response);
      });
  }

  // Sensor methods
  async getSensorsBySubstation(substationUid?: string): Promise<FlexStation[]> {
    return this.client
      .get(`/v1/substations/${substationUid}/sensors`)
      .then((response: MdslResponse) => {
        // Some endpoints return { data: { data: { flex_stations: [...] } } }
        // Normalize to return the flex_stations array or an empty array.
        try {
          const d = response?.data;
          if (d?.data?.flex_stations && Array.isArray(d.data.flex_stations)) {
            return d.data.flex_stations;
          }
          // Fallback: if normalization can find an array, return it (keeps compatibility)
          const normalized = PropAIApi.normalizeCollection(response);
          return normalized;
        } catch (e) {
          return [];
        }
      });
  }

  async getStationAdapterConfig(flexstationUid: string): Promise<any | null> {
    return this.client
      .get(`/v1/flexstations/${flexstationUid}/adapter-config`)
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        // 404 return null
        if (error.status === 404) {
          return null;
        }
        logger.error("getStationAdapterConfig error", { error });
        throw new Error(error.response?.data?.error?.message || "Unknown error");
      });
  }

  async getFlexStationParameters(flexstationUid: string): Promise<FlexStationParameters | null> {
    return this.client
      .get(`/v1/flexstations/${flexstationUid}/parameters`)
      .then((response: MdslResponse) => {
        return response.data.data;
      })
      .catch((error) => {
        logger.error("getFlexStationParameters error", { error });
        return null;
      });
  }

  async updateFlexStationParameters(
    flexstationUid: string,
    body: FlexStationParameters
  ): Promise<any> {
    const url = `/v1/flexstations/${flexstationUid}/parameters`;
    return this.client
      .patch(url, body)
      .then((response) => {
        logger.debug("Update flexstation parameters success", response.data);
        return response;
      })
      .catch((error) => logger.error("update flexstation parameters error", { error }));
  }

  async updateStationAdapterConfig(
    stationUid: string,
    body: FlexstationAdapterConfigBody
  ): Promise<any> {
    const url = `/v1/flexstations/${stationUid}/adapter-config`;

    return this.client
      .patch(url, body)
      .then((response) => {
        logger.debug("Updated flexstation adapter config success", response.data);
        return response;
      })
      .catch((error) => logger.error("Upate flexstation adapter config", { error }));
  }

  async getInfoBlocksPerSubstation(
    substationUid?: string,
    blockNames?: string[]
  ): Promise<InfoDataResponse> {
    return this.client
      .get(`/v1/substations/${substationUid}/info-blocks/core`, {
        params: {
          blockNames: blockNames?.join(","),
        },
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        logger.error("getInfoBlocksPerSubstation error", { error });
        throw new Error(error.response?.data?.error || "Unknown error");
      });
  }

  async getSensorData(
    substationUid?: string,
    sensorUid?: string,
    fromDate?: string,
    toDate?: string
  ): Promise<SensorDataPerSubstation[]> {
    logger.debug("getSensorDataPerSubstation request", {
      substationUid,
      sensorUid,
      fromDate,
      toDate,
    });
    return this.client
      .get(`/v1/substations/${substationUid}/sensors/${sensorUid}/readings`, {
        params: {
          "datetime[ge]": fromDate,
          "datetime[lt]": toDate,
        },
      })
      .then((response) => {
        return PropAIApi.normalizeCollection(response);
      });
  }

  /**
   * Get average sensor data for all sensors in a building/substation
   * Returns time-series data with averaged values across all sensors
   */
  async getAverageSensorData(
    substationUid?: string,
    sensorTypeTechnicalName?: string,
    fromDate?: string,
    toDate?: string
  ): Promise<SensorDataPerSubstation[]> {
    return this.client
      .get(`/v1/substations/${substationUid}/sensors/type/${sensorTypeTechnicalName}/average`, {
        params: {
          "datetime[ge]": fromDate,
          "datetime[lt]": toDate,
        },
      })
      .then((response) => {
        return PropAIApi.normalizeCollection(response);
      });
  }

  /**
   * Backend-computed indoor-temperature averages (1h / 24h / 7d / 30d) for a
   * flexstation, in a single call (PropAI's flexstation-scoped variant).
   */
  async getTemperatureAverages(
    substationUid: string,
    flexstationUid?: string | null
  ): Promise<TemperatureAverages> {
    if (!flexstationUid) return normalizeTemperatureAverages(null);
    return this.client
      .get(`/v1/substations/${substationUid}/flexstations/${flexstationUid}/temperature-averages`)
      .then((response) => normalizeTemperatureAverages(response?.data?.data))
      .catch((error) => {
        logger.error("getTemperatureAverages error", { error, substationUid, flexstationUid });
        return normalizeTemperatureAverages(null);
      });
  }

  async updateSensorMetaData(
    flexstationUid?: string,
    sensorUid?: string,
    apartment?: string,
    enabled?: boolean,
    floor?: number,
    streetAddress?: string
  ): Promise<{ updated_at: string } | null> {
    return this.client
      .patch(`/v1/flexstations/${flexstationUid}/sensors/${sensorUid}/meta-data`, {
        apartment,
        enabled,
        floor,
        street_address: streetAddress,
      })
      .then((response) => {
        const value = response?.data?.data;

        return value?.updated_at ? value : null;
      })
      .catch((error) => {
        logger.error("updateSensorMetaData error", {
          error,
          flexstationUid,
          sensorUid,
        });
        return null;
      });
  }

  // Meter data methods (implements ISharedDataApi.getMeterData)
  async getMeterData(
    substationUid: string,
    components: string[] = ["heat_energy", "returntemp", "supplytemp"],
    fromDate?: string,
    toDate?: string
  ): Promise<MeterDataComponent[]> {
    const params = new URLSearchParams();

    if (components.length > 0) {
      params.append("component[in]", components.join(","));
    }
    if (fromDate) {
      params.append("datetime[ge]", fromDate);
    }
    if (toDate) {
      params.append("datetime[lt]", toDate);
    }

    const url = `/v1/meter-data/${substationUid}${params.toString() ? `?${params.toString()}` : ""}`;

    return this.client
      .get(url)
      .then((response: MdslResponse) => {
        const collection = response.data.data.collection as any[];
        return collection.map((item) => ({
          ...item,
          component: item.component_name,
          meter: item.meter_name,
          stage: item.stage_name,
          variant: item.variant_name,
        })) as MeterDataComponent[];
      })
      .catch((error) => {
        logger.error("getMeterData error", { error });
        throw new Error(error.response?.data?.error?.message || "Unknown error");
      });
  }

  async getInfoBlocks(substationUid: string, blockNames: string[]): Promise<InfoBlocksResponse> {
    const raw = await this.getInfoBlocksPerSubstation(substationUid, blockNames);
    return { collection: (raw as any)?.collection ?? [] };
  }

  async getFilteredSensors(substationUid: string, sensorTypes: string[]): Promise<Sensor[]> {
    const flexStations = await this.getSensorsBySubstation(substationUid);
    const flattened = (flexStations ?? []).flatMap(
      (flexStation: any) => flexStation?.sensors ?? []
    );
    if (sensorTypes.length === 0) return flattened;
    return flattened.filter((sensor: Sensor) =>
      sensorTypes.includes(sensor.sensor_type_technical_name)
    );
  }

  // equivalent power aggregate for substation
  async getEquivalentPowerAggregateBySubstation(
    substationUid: string,
    fromDate?: string,
    toDate?: string
  ): Promise<EquivalentPowerAggregate[]> {
    return this.client
      .get(`/v1/substations/${substationUid}/equivalent-power-aggregate`, {
        params: {
          "datetime[ge]": fromDate,
          "datetime[lt]": toDate,
        },
      })
      .then((response: MdslResponse) => {
        return response.data.data.collection;
      });
  }

  // Invitation methods
  async acceptInvitation(
    invitationCode: string
  ): Promise<{ organization_uid: string; organization_name: string }> {
    return this.client
      .get("/v1/admin/invitations/accept", {
        params: { token: invitationCode },
      })
      .then((response) => {
        logger.debug("acceptInvitation success", { token: invitationCode });
        return response.data.data;
      })
      .catch((error) => {
        logger.error("acceptInvitation error", { error });
        throw new Error(error.response?.data?.error || "Unknown error");
      });
  }

  // Radiator curve methods
  async getRadiatorCurvesBySubstation(substationUid: string): Promise<RadiatorCurve[]> {
    logger.debug("getRadiatorCurvesBySubstation request", { substationUid });
    return this.client
      .get(`/v1/substations/${substationUid}/radiator-curves`)
      .then((response: MdslResponse) => {
        // Normalize to the expected array shape
        const normalized = PropAIApi.normalizeCollection(response);
        return normalized.length ? normalized : [];
      })
      .catch((error) => {
        logger.error("getRadiatorCurvesBySubstation error", { error, substationUid });
        throw new Error(error.response?.data?.error?.message || "Failed to fetch radiator curves");
      });
  }

  async getRadiatorCurveByFlexstation(
    substationUid: string,
    flexstationUid: string
  ): Promise<RadiatorCurve | null> {
    logger.debug("getRadiatorCurveByFlexstation request", { substationUid, flexstationUid });
    return this.client
      .get(`/v1/substations/${substationUid}/flexstations/${flexstationUid}/radiator-curve`)
      .then((response: MdslResponse) => {
        return response.data.data || null;
      })
      .catch((error) => {
        logger.error("getRadiatorCurveByFlexstation error", {
          error,
          substationUid,
          flexstationUid,
        });
        // Return null instead of throwing for optional data
        return null;
      });
  }

  async getSubstationById(substationUid: string): Promise<Substation | null> {
    logger.debug("getSubstationById request", { substationUid });
    return this.client
      .get(`/v1/substations/${substationUid}`)
      .then((response: MdslResponse) => {
        return response.data.data;
      })
      .catch((error) => {
        logger.error("getSubstationById error", { error, substationUid });
        return null;
      });
  }

  async getSubstation(substationUid: string): Promise<Substation | null> {
    // Alias for getSubstationById for compatibility
    return this.getSubstationById(substationUid);
  }

  async getFlexStationsBySubstation(substationUid: string): Promise<FlexStation[]> {
    return this.client
      .get(`/v1/substations/${substationUid}/flexstations`)
      .then((response: MdslResponse) => {
        return response.data.data.collection;
      })
      .catch((error) => {
        logger.error("getFlexStationsBySubstation error", { error, substationUid });
        return [];
      });
  }

  /**
   * Get the chronological event log for a flexstation.
   */
  async getFlexStationEventLog(
    substationUid?: string,
    flexstationUid?: string,
    // Accepted for parity with the platform (network-scoped) API; PropAI's /v1
    // endpoint is not network-scoped, so this only feeds the error log.
    networkUid?: string
  ): Promise<EventLogEntry[]> {
    return this.client
      .get(`/v1/substations/${substationUid}/flexstations/${flexstationUid}/event-log`)
      .then((response) => {
        return (response?.data?.data?.events ?? []) as EventLogEntry[];
      })
      .catch((error) => {
        logger.error("getFlexStationEventLog error", {
          error,
          substationUid,
          flexstationUid,
          networkUid,
        });
        return [];
      });
  }

  async getFlexControlRequestsData(
    flexstationUid: string,
    fromDate?: string,
    toDate?: string
  ): Promise<FlexControlRequest[]> {
    return this.client
      .get(`/v1/flexstations/${flexstationUid}/flex-control-requests-data`, {
        params: {
          "datetime[ge]": fromDate,
          "datetime[lt]": toDate,
        },
      })
      .then((response) => {
        return PropAIApi.normalizeCollection(response);
      });
  }

  async getEnergyKpisBySubstation(
    substationUid: string,
    fromDate?: string
  ): Promise<EnergyKpiResponse> {
    return this.client
      .get(`/v1/substations/${substationUid}/energy/kpis`, {
        params: {
          "datetime[ge]": fromDate,
        },
      })
      .then((response) => {
        return response.data.data;
      });
  }

  // ADMIN

  async getPropertyOrganizationUsers(
    propertyOrganizationUid?: string
  ): Promise<PropertyOrganizationUser[]> {
    return this.client
      .get(`/v1/admin/property-organizations/${propertyOrganizationUid}/users`)
      .then((response: MdslResponse) => {
        return response.data.data.collection || [];
      })
      .catch((error) => {
        logger.error("getPropertyOrganizationUsers error", { error, propertyOrganizationUid });
        throw new Error(
          error.response?.data?.data?.error?.message ||
            "Failed to fetch property organization users"
        );
      });
  }

  async addPropertyOrganizationUser(
    propertyOrganizationUid: string,
    email: string,
    locale?: string
  ): Promise<any> {
    return this.client
      .post(`/v1/admin/property-organizations/${propertyOrganizationUid}/users`, {
        email,
        locale,
      })
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        logger.error("addPropertyOrganizationUser error", {
          error,
          propertyOrganizationUid,
          email,
          locale,
        });
        throw new Error(
          error.response?.data?.data?.error?.message ||
            "Failed to add user to property organization"
        );
      });
  }

  async removePropertyOrganizationUser(
    propertyOrganizationUid: string,
    userUid: string
  ): Promise<void> {
    return this.client
      .delete(`/v1/admin/property-organizations/${propertyOrganizationUid}/users/${userUid}`)
      .then(() => {
        logger.debug("removePropertyOrganizationUser success", {
          propertyOrganizationUid,
          userUid,
        });
      })
      .catch((error) => {
        logger.error("removePropertyOrganizationUser error", {
          error,
          propertyOrganizationUid,
          userUid,
        });
        throw new Error(
          error.response?.data?.data?.error?.message ||
            "Failed to remove user from property organization"
        );
      });
  }

  /**
   * Get aggregated indoor climate readings (avg / min / max / percentiles)
   * across all sensors in a flexstation, bucketed by timestep.
   */
  async getFlexstationClimateSeries(
    flexstationUid: string,
    fromDate?: string,
    toDate?: string
  ): Promise<FlexstationClimateSeriesPoint[]> {
    return this.client
      .get(`/v1/flexstations/${flexstationUid}/climate-series`, {
        params: {
          "datetime[ge]": fromDate,
          "datetime[lt]": toDate,
        },
      })
      .then((response) => {
        return (response?.data?.data?.data ?? []) as FlexstationClimateSeriesPoint[];
      })
      .catch((error) => {
        logger.error("getFlexstationClimateSeries error", { error, flexstationUid });
        return [];
      });
  }

  /**
   * Get live insights for a flexstation
   * @param flexstationUid - The UID of the flexstation
   * @returns Insights response
   */
  async getFlexStationLiveInsights(flexstationUid: string): Promise<LiveInsightsResponse> {
    return this.client
      .get(`/v1/flexstations/${flexstationUid}/live-insights`)
      .then((response) => {
        return response.data.data;
      })
      .catch((error) => {
        logger.error("getFlexStationLiveInsights error", { error, flexstationUid });
        throw new Error(
          error.response?.data?.data?.error?.message || "Failed to fetch live insights"
        );
      });
  }

  /**
   * Get urgent notifications/alerts
   * @param params - Query parameters for filtering alerts
   * @returns Urgent notifications response
   */
  async getUrgentNotifications(params: {
    status_filter?: string;
    substation_uid?: string;
    organization_uid?: string;
    network_uid?: string;
    limit?: number;
    offset?: number;
  }): Promise<UrgentNotificationsResponse> {
    return this.client
      .get("/v1/notifications/urgent", {
        params: {
          status_filter: params.status_filter || "all",
          substation_uid: params.substation_uid,
          organization_uid: params.organization_uid,
          network_uid: params.network_uid,
          limit: params.limit || 100,
          offset: params.offset || 0,
        },
      })
      .then((response) => {
        // Handle both response formats: direct { notifications, total } or nested { data: { notifications, total } }
        const data = response.data;
        if (data.notifications && typeof data.total === "number") {
          return data;
        }
        if (data.data?.notifications && typeof data.data?.total === "number") {
          return data.data;
        }
        // Fallback to empty response
        return { notifications: [], total: 0 };
      })
      .catch((error) => {
        logger.error("getUrgentNotifications error", { error, params });
        throw new Error("Failed to fetch urgent notifications");
      });
  }
}
